<script lang="ts">
    import {getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {RangeValueEditorOptions} from './types';
    import type {RangeValueEditorModel} from './types';
    import type {RangeValueEditorLocalizations} from './types';
    import type {ValueEditorValidations} from '../../../kp-value-editor/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormGroup} from '../../forms/form-group';
    import {FormControlBuilder} from '../../forms/form-control';
    import {getContext, onDestroy, onMount} from 'svelte';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {FormGroupBuilder} from '../../forms/form-group';
    import {formControls} from '../../forms/use.form-controls';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {isDefined} from 'shared/utils/custom-utils';
    import {createInputDescription} from '../../description/utils';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {emptyAsNullParser} from '../_shared/editors-utils';
    import {ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import {
        nullAsExtremeFormatter,
        getPitPointsPosition,
        roundToClosestMultipleOfStep,
        parseInputs,
        setToggleAccessible,
        addMissingExtremeModifier,
        extremesAsNullModifier
    } from './utils';

    export let model: RangeValueEditorModel;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: RangeValueEditorOptions;
    export let validations: ValueEditorValidations;

    ignoreUnusedProperties(placeholder, validations);

    const logger = getLogger();
    const localize: EditorsLocalizationFunction<RangeValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const labelContext = getValueEditorLabelContext();

    let fromSliderInstance: HTMLInputElement;
    let toSliderInstance: HTMLInputElement;
    const pitStyleCache = getPitPointsPosition(options);


    const formControlFrom = FormControlBuilder.for(`${editorId}-from`, `${editorName}-from`, model?.from)
        .withFormatters([nullAsExtremeFormatter(options, options.min)])
        .withOptions(formControlOptions)
        .build(logger);

    const formControlTo = FormControlBuilder.for(`${editorId}-to`, `${editorName}-to`, model?.to)
        .withFormatters([nullAsExtremeFormatter(options, options.max)])
        .withOptions(formControlOptions)
        .build(logger);

    const formGroupBuilder = new FormGroupBuilder<RangeValueEditorModel>();
    formGroupBuilder.registerFormControl(formControlFrom);
    formGroupBuilder.registerFormControl(formControlTo);
    formGroupBuilder.addModifiers([emptyAsNullParser(options), extremesAsNullModifier(options), addMissingExtremeModifier(options)]);

    const formControl = formGroupBuilder.build(editorId, editorName, new Map(
        [[`${editorName}-from`, 'from'], [`${editorName}-to`, 'to']]
    ));

    const modelSubscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    onMount(() => {
        setToggleAccessible(toSliderInstance);
    });

    $: if (toSliderInstance) {
        fillSlider(toSliderInstance, model, isDisabled);
    }

    $: toModelOrMax = options.extremesAsNull ? model?.to ?? options.max : model?.to;
    $: fromModelOrMin = options.extremesAsNull ? model?.from ?? options.min : model?.from;

    onDestroy(() => {
        modelSubscription.unsubscribe();
    });

    export function getFormController(): FormGroup<RangeValueEditorModel> {
        return formControl;
    }

    function controlFromSlider(): void {
        const [from, to] = parseInputs(fromSliderInstance, toSliderInstance);
        if (from > to) {
            fromSliderInstance.value = String(to);
        } else {
            fromSliderInstance.value = String(from);
        }
    }

    function controlToSlider(): void {
        const [from, to] = parseInputs(fromSliderInstance, toSliderInstance);
        setToggleAccessible(toSliderInstance);
        if (from <= to) {
            toSliderInstance.value = String(to);
        } else {
            toSliderInstance.value = String(from);
        }
    }

    function fillSlider(sliderElementRef: HTMLInputElement, modelValue: RangeValueEditorModel, disabled: boolean): void {
        const BRAND_PRIMARY = '#337ab7'; // @brand-primary
        const BRAND_PRIMARY_HALF_OPACITY = '#9abddb'; // @brand-primary + opacity 0.5
        const SLIDER_BACKGROUND_COLOR = '#c6c6c6';
        const SLIDER_BACKGROUND_COLOR_HALF_OPACITY = '#e3e3e3'; // #c6c6c6 + opacity 0.5

        const sliderFillColor = disabled ? BRAND_PRIMARY_HALF_OPACITY : BRAND_PRIMARY;
        const sliderBackgroundColor = disabled ? SLIDER_BACKGROUND_COLOR_HALF_OPACITY : SLIDER_BACKGROUND_COLOR;

        const from = modelValue?.from ?? options.min;
        const to = modelValue?.to ?? options.max;
        const rangeDistance = options.max - options.min;
        const fromPosition = from - options.min;
        const toPosition = to - options.min;
        toSliderInstance.style.background =
            `linear-gradient(
                    to right,
                    ${sliderBackgroundColor} 0%,
                    ${sliderBackgroundColor} ${(fromPosition) / (rangeDistance) * 100}%,
                    var(--slider-fill-color, ${sliderFillColor}) ${((fromPosition) / (rangeDistance)) * 100}%,
                    var(--slider-fill-color, ${sliderFillColor}) ${(toPosition) / (rangeDistance) * 100}%,
                    ${sliderBackgroundColor} ${(toPosition) / (rangeDistance) * 100}%,
                    ${sliderBackgroundColor} 100%
            )`;
    }

    function setFrom(value: number) {
        if (value < options.min) {
            value = options.min;
        }
        if (value > toModelOrMax) {
            value = toModelOrMax;
        }
        formControlFrom.setViewValue(roundToClosestMultipleOfStep(value, options.step, options.min));
        formControlFrom.setTouched();
    }

    function setTo(value: number) {
        if (value > options.max) {
            value = options.max;
        }
        if (value < fromModelOrMin) {
            value = fromModelOrMin;
        }

        formControlTo.setViewValue(roundToClosestMultipleOfStep(value, options.step, options.min));
        formControlTo.setTouched();
    }
</script>

<div>
    <svelte:component this={options.currentValueComponent}
                      from={fromModelOrMin}
                      to={toModelOrMax}
                      on:set-from={(event) => setFrom(event.detail)}
                      on:set-to={(event) => setTo(event.detail)}
                      {editorId}
                      {editorName}
                      {size}
                      {options}
                      {isDisabled}/>
</div>

<div class="sliders-control size-{size}" id={editorId} role="group" use:ariaLabelledby={labelContext} data-main-input>
    <input bind:this={fromSliderInstance}
           id={editorId ? `${editorId}-from` : null}
           name="{editorName}-from"
           class="from-slider"
           type="range"
           disabled={isDisabled}
           min={options.min}
           max={options.max}
           step={options.step}
           aria-label={localize('from')}
           use:ariaErrormessage={formControl}
           use:ariaInvalid={formControl}
           use:formControls={formControlFrom}
           use:focusOnMount={isFocused}
           use:touched={{formControl: formControlFrom}}
           use:ariaDescribedby={inputDescription}
           on:input={controlFromSlider}
           data-input-from/>

    <input bind:this={toSliderInstance}
           class="to-slider"
           id={editorId ? `${editorId}-to` : null}
           name="{editorName}-to"
           type="range"
           disabled={isDisabled}
           min={options.min}
           max={options.max}
           step={options.step}
           aria-label={localize('to')}
           use:ariaErrormessage={formControl}
           use:ariaInvalid={formControl}
           use:formControls={formControlTo}
           use:touched={{formControl: formControlTo}}
           use:ariaDescribedby={inputDescription}
           on:input={controlToSlider}
           data-input-to/>

    {#if isDefined(options.pitPoints)}
        <!-- TODO: for next refactoring use native datalist: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range#adding_tick_marks-->
        <div class="pit-container" role="presentation">
            {#each options.pitPoints as pitPoint, index}
                <div class="pit"
                     class:disabled={isDisabled}
                     style:position={pitStyleCache[index].position}
                     style:top={pitStyleCache[index].top}
                     style:left={pitStyleCache[index].left}>

                    <div class="pit-centering-wrapper">
                        <div class="pit-centering-content">
                            {pitPoint}
                        </div>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>
<Description {inputDescription}/>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    @disabled-opacity: 0.65;

    .pit-container {
        position: relative;
        margin: 0 0.5em;
    }

    .pit {
        padding-top: 3px;

        .pit-centering-wrapper {
            position: absolute;
            left: 50%;

            .pit-centering-content {
                position: relative;
                left: -50%;
                font-size: 14px;
            }
        }

        &.disabled {
            opacity: @disabled-opacity;
        }
    }

    .sliders-control {
        position: relative;
        min-height: 2em;
        height: 1em;
        overflow: visible;
        margin: 1em 0.5em 0 0.5em;
    }

    .sliders-control .from-slider {
        height: 0;
        z-index: 1;
        transform: translateY(2px);
    }


    input[type="range"] {
        -webkit-appearance: none;
        appearance: none;
        height: 0.2em;
        width: 100%;
        position: absolute;
        background-color: @gray-lighter;
        pointer-events: none;
    }

    input[type="range"]:disabled {
        cursor: not-allowed;
        pointer-events: auto;
    }

    /* styles of slider endpoint buttons */
    .slider-endpoints() {
        -webkit-appearance: none;
        appearance: none;
        pointer-events: auto;
        width: 1em;
        height: 1em;
        background-color: @body-bg;
        border-radius: 50%;
        box-shadow: 0 0 0 1px @gray-lighter;
        cursor: pointer;
    }

    .slider-endpoints-enabled() {
        &:hover {
            background: @gray-lighter;
        }

        &:active {
            box-shadow: inset 0 0 3px @brand-primary, 0 0 9px @brand-primary;
            -webkit-box-shadow: inset 0 0 3px @brand-primary, 0 0 9px @brand-primary;
        }
    }

    .slider-endpoints-disabled() {
        cursor: not-allowed;
    }

    /* vendor pseudo-elements need to be separate, otherwise the styles wont work */
    input[type="range"]::-webkit-slider-thumb {
        .slider-endpoints();
    }

    /* vendor pseudo-elements need to be separate, otherwise the styles wont work */
    input[type="range"]::-moz-range-thumb {
        .slider-endpoints();
    }

    /* vendor pseudo-elements need to be separate, otherwise the styles wont work */
    input[type="range"]:not(:disabled)::-webkit-slider-thumb {
        .slider-endpoints-enabled();
    }

    /* vendor pseudo-elements need to be separate, otherwise the styles wont work */
    input[type="range"]:not(:disabled)::-moz-range-thumb {
        .slider-endpoints-enabled();
    }

    /* vendor pseudo-elements need to be separate, otherwise the styles wont work */
    input[type="range"]:disabled::-webkit-slider-thumb {
        .slider-endpoints-disabled();
    }

    /* vendor pseudo-elements need to be separate, otherwise the styles wont work */
    input[type="range"]:disabled::-moz-range-thumb {
        .slider-endpoints-disabled();
    }

    .size-xs {
        font-size: 1em;
    }

    .size-sm {
        font-size: 1.35em;
    }

    .size-md {
        font-size: 1.7em;
    }
</style>